// HypothesisCorrelationLab.jsx
// Cross-asset correlation lab for the Antifragility Hypothesis System.
//
// Data path (STRICT — no client-side ticker mangling):
//   frontend sends ticker EXACTLY as it appears in the hypothesis positions
//   or in MACRO_GROUPS. The :6062 backend has `resolveFetch()` + universe
//   aliases, and :8080 has `tickerInfo` CSV — both handle .SA suffix for
//   B3, pass-through for US/EU/commodities/FX/rates. Frontend must NEVER
//   guess.
//
// Grade (also strict):
//   Tier 3 (:8080) serves MONTHLY OHLC. Historical bars are first-of-month,
//   in-progress bar uses today's date. Different tickers get fetched at
//   slightly different times → today's date can vary by seconds. So we
//   collapse every candle to `YYYY-MM` before comparing / correlating.
//   Sensor sparklines already come as `{month: "2023-07", value: N}` so
//   they slot into the same grid without translation.
//
// Cache non-poisoning:
//   Empty result ≠ cached success. `historyCache` stores only real data;
//   a `failedTickers` Set marks known-bad tickers for the SESSION but
//   allows retry on the next batch call so transient yfinance failures
//   heal on interaction.

import { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from "recharts";

// ── Mobile detection ──────────────────────────────────────────────────────
// Single-breakpoint hook, resize-aware. Desktop styles remain the source
// of truth; mobile branches only override the values that would break at
// narrow widths (padding, chart height, y-axis width, grid columns).
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpoint;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// ── Endpoints ─────────────────────────────────────────────────────────────
const THESIS_API = "/api/thesis";
const HISTORY_API = "/api/history";

// ── Palette ───────────────────────────────────────────────────────────────
const C = {
  bg: "#0a0a0a", card: "#111118",
  border: "#1e1e1e", borderSoft: "#181818",
  gold: "#c9a227", purple: "#6660fe",
  green: "#4ade80", red: "#ef4444",
  amber: "#fbbf24", cyan: "#22d3ee",
  orange: "#fb923c", blue: "#60a5fa",
  white: "#e8e8f0",
  dim: "rgba(232,232,240,0.50)",
  dim2: "rgba(232,232,240,0.35)",
  dim3: "rgba(232,232,240,0.20)",
};

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
`;

// ── Macro overlays (always available regardless of hypothesis) ─────────
const MACRO_GROUPS = [
  {
    id: "fx", label: "FX",
    items: [
      { ticker: "BRL=X", label: "USD/BRL", color: "#f472b6" },
      { ticker: "DX-Y.NYB", label: "DXY", color: "#c084fc" },
      { ticker: "EURBRL=X", label: "EUR/BRL", color: "#fb7185" },
    ],
  },
  {
    id: "commodities", label: "Commodities",
    items: [
      { ticker: "GC=F", label: "Ouro", color: "#fbbf24" },
      { ticker: "CL=F", label: "WTI", color: "#fb923c" },
      { ticker: "BZ=F", label: "Brent", color: "#f97316" },
      { ticker: "ZS=F", label: "Soja", color: "#84cc16" },
      { ticker: "KC=F", label: "Café", color: "#78350f" },
    ],
  },
  {
    id: "rates", label: "Juros / Medo",
    items: [
      { ticker: "^TNX", label: "US 10Y", color: "#38bdf8" },
      { ticker: "^VIX", label: "VIX", color: "#ef4444" },
    ],
  },
  {
    id: "indices", label: "Índices",
    items: [
      { ticker: "^BVSP", label: "IBOV", color: "#22d3ee" },
      { ticker: "^GSPC", label: "S&P", color: "#60a5fa" },
      { ticker: "EWZ", label: "EWZ", color: "#22c55e" },
    ],
  },
];

const ALL_MACRO = MACRO_GROUPS.flatMap(g => g.items);

// ── Time ranges (in months for monthly data) ──────────────────────────────
const RANGES = [
  { id: "6m", label: "6M", months: 6 },
  { id: "1y", label: "1A", months: 12 },
  { id: "3y", label: "3A", months: 36 },
  { id: "5y", label: "5A", months: 60 },
  { id: "10y", label: "10A", months: 120 },
];

// ═══════════════════════════════════════════════════════════════════════
// STRICT DATA LAYER
// ═══════════════════════════════════════════════════════════════════════

// Session-scoped cache. Key = ticker exactly as sent. Value = monthly
// series [{ ym: "2026-07", close }, ...]. Absence means "never fetched"
// or "last fetch failed" — we retry on next call.
const historyCache = new Map();

// Tickers whose most recent fetch attempt returned empty or errored.
// Allowed to retry, but tracked so the UI can warn.
const failedTickers = new Set();

// Reduce a raw candle array to one point per month (last close wins).
// Sorted ascending by ym.
function toMonthly(candles) {
  if (!Array.isArray(candles) || !candles.length) return [];
  const byMonth = new Map();
  for (const c of candles) {
    if (!c || c.close == null || c.date == null) continue;
    const ym = String(c.date).slice(0, 7); // "YYYY-MM"
    if (ym.length !== 7) continue;
    byMonth.set(ym, c.close); // last write wins → latest date in month
  }
  return Array.from(byMonth.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([ym, close]) => ({ ym, close }));
}

// Sensor sparklines from :8095 arrive in several formats depending on
// which upstream source (:8300 social_service, hardcoded JSON, Genoma)
// populated them:
//   "2023-07"        → ISO monthly, ideal case
//   "2010"           → year-only annual data (WHO, V-Dem, TMI)
//   "2026-Jun"       → year + 3-letter month abbrev (CNES)
//   "2026-06-01"     → full ISO date (rare)
// Normalize everything to canonical "YYYY-MM". Year-only snaps to June
// (mid-year) as an arbitrary-but-stable convention for annual data on
// a monthly axis. Returns null when parse fails.
const MONTH_ABBREV = {
  jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
  jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
  // Portuguese variants that occasionally slip through
  fev: "02", abr: "04", mai: "05", ago: "08", set: "09", out: "10", dez: "12",
};

function normalizeMonth(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // YYYY-MM or YYYY-MM-DD
  let m = s.match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);
  if (m) {
    const mi = parseInt(m[2], 10);
    if (mi >= 1 && mi <= 12) return `${m[1]}-${String(mi).padStart(2, "0")}`;
    return null;
  }
  // YYYY-Mon (English or Portuguese 3-letter abbrev)
  m = s.match(/^(\d{4})-([A-Za-zÀ-ÿ]{3,})$/);
  if (m) {
    const mm = MONTH_ABBREV[m[2].toLowerCase().slice(0, 3)];
    if (mm) return `${m[1]}-${mm}`;
    return null;
  }
  // Year only → snap to June (mid-year, no bias toward publication month)
  m = s.match(/^(\d{4})$/);
  if (m) return `${m[1]}-06`;
  return null;
}

// Take a raw sparkline of {month, value} points from the backend and
// return a clean, sorted, dedup'd series in canonical form.
//
// Backend currently has three data quality issues in social sensors:
//   1. Heterogeneous month formats (year-only, ISO, English abbrev)
//   2. Reverse-chronological order for several indicators
//   3. Duplicated entity concatenation (entity_id="all" appends every
//      city's series into one sparkline)
// This function collapses (3) via last-write-wins per canonical month
// key, and repairs (1) and (2). Backend fix is still recommended but
// the frontend no longer depends on backend hygiene.
function normalizeSensorSparkline(spark) {
  if (!Array.isArray(spark) || !spark.length) return [];
  const byMonth = new Map();
  for (const p of spark) {
    if (!p || p.value == null) continue;
    const ym = normalizeMonth(p.month);
    if (!ym) continue;
    byMonth.set(ym, Number(p.value)); // last write wins on collision
  }
  return Array.from(byMonth.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([ym, value]) => ({ ym, value }));
}

// Distinct palette for up to 5 concurrent sensor lines. Falls back to
// the hypothesis color once all 5 are used.
const SENSOR_COLORS = ["#22d3ee", "#fbbf24", "#84cc16", "#f472b6", "#a78bfa"];

async function fetchHistoryBatch(tickers) {
  // De-dup and filter out already-cached tickers. Failed ones get retried.
  const unique = Array.from(new Set(tickers.filter(Boolean)));
  const need = unique.filter(t => !historyCache.has(t));

  if (need.length > 0) {
    try {
      const url = `${HISTORY_API}/api/history?tickers=${encodeURIComponent(need.join(","))}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`http ${r.status}`);
      const j = await r.json();
      const series = j.series || {};
      const missing = new Set(j.missing || []);

      for (const t of need) {
        const entry = series[t];
        const candles = entry && Array.isArray(entry.candles) ? entry.candles : [];
        const monthly = toMonthly(candles);

        if (missing.has(t) || monthly.length === 0 || entry?.error) {
          failedTickers.add(t);
          // Do NOT cache empty — let retry happen on next call.
        } else {
          historyCache.set(t, monthly);
          failedTickers.delete(t);
        }
      }
    } catch (e) {
      console.warn("[HypCorrelationLab] batch fetch:", e);
      // Network error — don't poison cache. Mark failed for UI hint.
      for (const t of need) failedTickers.add(t);
    }
  }

  const result = {};
  for (const t of unique) result[t] = historyCache.get(t) || [];
  return result;
}

// ── Math helpers ──────────────────────────────────────────────────────────
function filterMonths(series, months) {
  if (!series.length || !months) return series;
  // Cutoff = current YYYY-MM shifted back by `months`.
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
  const cy = cutoff.getFullYear();
  const cm = String(cutoff.getMonth() + 1).padStart(2, "0");
  const cutoffYm = `${cy}-${cm}`;
  return series.filter(p => p.ym >= cutoffYm);
}

function toReturns(closes) {
  const r = [];
  for (let i = 1; i < closes.length; i++) {
    const prev = closes[i - 1];
    r.push(prev === 0 ? 0 : (closes[i] - prev) / prev);
  }
  return r;
}

function pearson(x, y) {
  const n = Math.min(x.length, y.length);
  if (n < 5) return null;
  let sx = 0, sy = 0;
  for (let i = 0; i < n; i++) { sx += x[i]; sy += y[i]; }
  const mx = sx / n, my = sy / n;
  let num = 0, dx2 = 0, dy2 = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i] - mx, dy = y[i] - my;
    num += dx * dy; dx2 += dx * dx; dy2 += dy * dy;
  }
  const den = Math.sqrt(dx2 * dy2);
  return den === 0 ? null : num / den;
}

function corrStrength(c) {
  const a = Math.abs(c);
  if (a >= 0.7) return { label: "FORTE", color: C.green };
  if (a >= 0.5) return { label: "MODERADA", color: C.amber };
  if (a >= 0.3) return { label: "FRACA", color: C.cyan };
  return { label: "RUÍDO", color: C.dim2 };
}

// ── Tooltip ───────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, overlayLookup, viewMode }) => {
  if (!active || !payload?.length) return null;
  const fmt = (v) => {
    if (v == null) return "—";
    if (viewMode === "pct") return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
    const abs = Math.abs(v);
    if (abs >= 1000) return v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
    if (abs >= 10) return v.toFixed(2);
    return v.toFixed(4);
  };
  return (
    <div style={{
      background: "rgba(7,7,10,0.96)", border: "1px solid #2a2a38",
      borderRadius: 6, padding: "10px 14px",
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      <div style={{ fontSize: 9, color: C.dim2, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => {
        const info = overlayLookup[p.dataKey];
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 2 }}>
            <span style={{ fontSize: 10, color: p.color || C.white }}>{info?.label || p.dataKey}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: p.color || C.white }}>
              {fmt(p.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function HypothesisCorrelationLab() {
  const [hypotheses, setHypotheses] = useState([]);
  const [details, setDetails] = useState({});
  const [activeHypId, setActiveHypId] = useState(null);
  const [rangeId, setRangeId] = useState("3y");
  const [activeOverlays, setActiveOverlays] = useState(["^BVSP", "GC=F", "BRL=X"]);
  const [seriesCache, setSeriesCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [discoveryCount, setDiscoveryCount] = useState(0);
  const [tickerStatus, setTickerStatus] = useState({}); // ticker → 'ok'|'failed'
  const [viewMode, setViewMode] = useState("pct"); // 'pct' = Δ% from window start (default), 'raw' = raw values

  const isMobile = useIsMobile();

  // Responsive dimensions — desktop numbers stay the source of truth.
  const PADX = isMobile ? 14 : 24;          // horizontal padding on card sections
  const CHART_H = isMobile ? 240 : 300;     // chart pixel height
  const Y_AXIS_W = isMobile ? 32 : (viewMode === "raw" ? 50 : 40);
  const TITLE_SIZE = isMobile ? 19 : 24;    // hypothesis title font
  const LABEL_MIN_W = isMobile ? 60 : 90;   // overlay picker category label
  const CORR_COLS = isMobile              // grid template for correlation table
    ? "minmax(0, 1fr) 62px 78px"          //   mobile: drop FORÇA column, tight
    : "1fr 90px 90px 100px";              //   desktop: OVERLAY / CORREL / FORÇA / RET

  const range = RANGES.find(r => r.id === rangeId) || RANGES[2];

  // ── Fetch hypothesis list ──
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${THESIS_API}/rr/v1/hypotheses`);
        const list = await res.json();
        setHypotheses(list);
        if (list.length > 0) setActiveHypId(list[0].id);
      } catch (e) {
        console.warn("[HypCorrelationLab] list:", e);
      }
    })();
  }, []);

  // ── Fetch active hypothesis detail ──
  useEffect(() => {
    if (!activeHypId || details[activeHypId]) return;
    (async () => {
      try {
        const res = await fetch(`${THESIS_API}/rr/v1/hypotheses/${activeHypId}`);
        const d = await res.json();
        setDetails(prev => ({ ...prev, [activeHypId]: d }));
      } catch (e) {
        console.warn("[HypCorrelationLab] detail:", e);
      }
    })();
  }, [activeHypId, details]);

  const activeDetail = details[activeHypId];

  // ── Tickers from active hypothesis (STRICT: no transformation) ──
  const hypTickers = useMemo(() => {
    if (!activeDetail) return { long: [], short: [], all: [] };
    // Basic sanity filter: reject strings with whitespace or absurd length.
    // Everything else passes through — backend decides validity.
    const clean = (arr) => (arr || []).filter(
      t => typeof t === "string" && t.trim() && !t.includes(" ") && t.length <= 12
    );
    const long = clean(activeDetail.positions?.long?.tickers);
    const short = clean(activeDetail.positions?.short?.tickers);
    return { long, short, all: [...long, ...short] };
  }, [activeDetail]);

  // ── Cross-hypothesis overlay options ──
  const crossHypOverlays = useMemo(() => {
    const seen = new Set(hypTickers.all);
    const items = [];
    Object.entries(details).forEach(([id, d]) => {
      if (id === activeHypId) return;
      const tickers = [
        ...(d.positions?.long?.tickers || []),
        ...(d.positions?.short?.tickers || []),
      ].filter(t => typeof t === "string" && !t.includes(" ") && t.length <= 12 && !seen.has(t));
      tickers.forEach(t => {
        if (!seen.has(t)) {
          seen.add(t);
          items.push({ ticker: t, label: `${t} · ${id}`, color: d.color || C.dim });
        }
      });
    });
    return items.slice(0, 20);
  }, [details, activeHypId, hypTickers.all]);

  // ── Fetch price history ──
  useEffect(() => {
    const needed = [...hypTickers.all, ...activeOverlays];
    if (needed.length === 0) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    fetchHistoryBatch(needed).then(map => {
      if (!alive) return;
      setSeriesCache(prev => ({ ...prev, ...map }));
      // Snapshot ticker status for the UI.
      const status = {};
      for (const t of needed) {
        status[t] = failedTickers.has(t) ? "failed" : (map[t]?.length ? "ok" : "failed");
      }
      setTickerStatus(prev => ({ ...prev, ...status }));
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [hypTickers.all, activeOverlays]);

  // ── Chart data + correlations (monthly grid) ──
  //
  // Flow: (1) normalize & window every input series → (2) union all months
  // across BOTH tickers and sensors so nothing gets silently dropped from
  // the x-axis → (3) build rows → (4) fill basket/overlays/sensors.
  //
  // Sensors run through normalizeSensorSparkline first, which repairs the
  // three known backend data quality issues (mixed month formats, reverse
  // sort, duplicated entity concatenation).
  const { chartData, correlations, returnsPct, lastRawValues, sensorKeys, basketMembers } = useMemo(() => {
    // 1a. Trim every ticker series to the range window.
    const monthly = {};
    const universe = [...hypTickers.long, ...activeOverlays];
    for (const t of universe) {
      const s = seriesCache[t];
      if (s?.length) {
        const trimmed = filterMonths(s, range.months);
        if (trimmed.length >= 2) monthly[t] = trimmed;
      }
    }

    // 1b. Normalize & window sensor sparklines. Each sensor becomes a
    //     [{ ym, value }] array that shares the same canonical month
    //     format as ticker data.
    const sensorSeries = []; // [{ key, name, color, points: [{ym, value}] }]
    if (activeDetail?.sensors) {
      activeDetail.sensors.forEach((s, si) => {
        const cleaned = normalizeSensorSparkline(s.reading?.sparkline);
        if (cleaned.length < 2) return;
        const trimmed = filterMonths(
          cleaned.map(p => ({ ym: p.ym, close: p.value })), // reuse filterMonths shape
          range.months
        ).map(p => ({ ym: p.ym, value: p.close }));
        if (trimmed.length < 2) return;
        sensorSeries.push({
          key: `sensor_${si}`,
          name: s.name,
          color: SENSOR_COLORS[si % SENSOR_COLORS.length],
          points: trimmed,
        });
      });
    }

    // 2. Union of month keys from EVERY source that will draw a line.
    const monthSet = new Set();
    Object.values(monthly).forEach(s => s.forEach(p => monthSet.add(p.ym)));
    sensorSeries.forEach(sen => sen.points.forEach(p => monthSet.add(p.ym)));
    const months = Array.from(monthSet).sort();

    if (months.length === 0) {
      return {
        chartData: [],
        correlations: {},
        returnsPct: {},
        lastRawValues: {},
        sensorKeys: [],
        basketMembers: { present: [], missing: [] },
      };
    }

    // 3. Fast lookup maps.
    const maps = {};
    for (const [t, s] of Object.entries(monthly)) {
      const m = new Map();
      s.forEach(p => m.set(p.ym, p.close));
      maps[t] = m;
    }

    // 4. Basket = equal-weight LONG tickers with data.
    const presentLong = hypTickers.long.filter(t => maps[t]);
    const missingLong = hypTickers.long.filter(t => !maps[t]);

    const rows = new Map();
    months.forEach(ym => rows.set(ym, { ym }));

    const rPct = {};      // Δ% from window start (kept independent of viewMode)
    const lastRaw = {};   // most recent raw value in the window
    let hasBasket = false;

    // Transform a level ratio (v/base) into the view-appropriate chart value.
    //   pct  → (v/base − 1) × 100    // 0 = start, +23 = +23%
    //   raw  → v                     // literal raw value
    const toChart = (v, base) => viewMode === "raw" ? v : (v / base - 1) * 100;

    if (presentLong.length > 0) {
      const basketPoints = [];
      months.forEach(ym => {
        const vals = presentLong.map(t => maps[t].get(ym)).filter(v => v != null && v > 0);
        if (vals.length === presentLong.length) {
          basketPoints.push({ ym, v: vals.reduce((a, b) => a + b, 0) / vals.length });
        }
      });
      if (basketPoints.length >= 2) {
        hasBasket = true;
        const base = basketPoints[0].v;
        basketPoints.forEach(({ ym, v }) => {
          const row = rows.get(ym);
          if (row) row.basket = toChart(v, base);
        });
        const lastV = basketPoints[basketPoints.length - 1].v;
        rPct._basket = ((lastV / base) - 1) * 100;
        lastRaw._basket = lastV;
      }
    }

    // 5. Each overlay: level scaled by its own base for pct mode, raw for raw mode.
    const corrs = {};
    for (const ovT of activeOverlays) {
      const map = maps[ovT];
      if (!map) continue;
      const points = months
        .map(ym => ({ ym, v: map.get(ym) }))
        .filter(p => p.v != null && p.v > 0);
      if (points.length < 2) continue;
      const base = points[0].v;
      points.forEach(({ ym, v }) => {
        const row = rows.get(ym);
        if (row) row[ovT] = toChart(v, base);
      });
      const lastV = points[points.length - 1].v;
      rPct[ovT] = ((lastV / base) - 1) * 100;
      lastRaw[ovT] = lastV;

      // Correlation is ALWAYS on returns of raw levels — independent of viewMode.
      // Pearson on levels would be spurious (co-trending series give high r).
      if (hasBasket) {
        const alignedBasket = [];
        const alignedOverlay = [];
        months.forEach(ym => {
          const bv = presentLong.map(t => maps[t].get(ym)).filter(v => v != null && v > 0);
          const ov = map.get(ym);
          if (bv.length === presentLong.length && ov != null) {
            alignedBasket.push(bv.reduce((a, b) => a + b, 0) / bv.length);
            alignedOverlay.push(ov);
          }
        });
        if (alignedBasket.length >= 6) {
          const corr = pearson(toReturns(alignedBasket), toReturns(alignedOverlay));
          if (corr != null) corrs[ovT] = corr;
        }
      }
    }

    // 6. Sensors: same viewMode logic; each sensor independent.
    const sKeys = [];
    for (const sen of sensorSeries) {
      const firstVal = sen.points[0].value;
      if (firstVal == null || firstVal === 0) continue;
      sKeys.push({ key: sen.key, name: sen.name, color: sen.color });
      sen.points.forEach(({ ym, value }) => {
        const row = rows.get(ym);
        if (row) row[sen.key] = toChart(value, firstVal);
      });
      const lastVal = sen.points[sen.points.length - 1].value;
      rPct[sen.key] = ((lastVal / firstVal) - 1) * 100;
      lastRaw[sen.key] = lastVal;
    }

    const sorted = Array.from(rows.values());
    return {
      chartData: sorted,
      correlations: corrs,
      returnsPct: rPct,
      lastRawValues: lastRaw,
      sensorKeys: sKeys,
      basketMembers: { present: presentLong, missing: missingLong },
    };
  }, [seriesCache, hypTickers.long, activeOverlays, range.months, activeDetail, viewMode]);

  // Discovery counter
  useEffect(() => {
    const strong = Object.values(correlations).filter(c => Math.abs(c) >= 0.5).length;
    if (strong > 0) setDiscoveryCount(prev => Math.max(prev, strong));
  }, [correlations]);

  const toggleOverlay = (ticker) => {
    setActiveOverlays(prev =>
      prev.includes(ticker) ? prev.filter(t => t !== ticker) : [...prev, ticker]
    );
  };

  const overlayLookup = useMemo(() => {
    const m = { basket: { label: activeDetail?.title || "Basket", color: activeDetail?.color || C.gold } };
    ALL_MACRO.forEach(i => { m[i.ticker] = i; });
    crossHypOverlays.forEach(i => { m[i.ticker] = i; });
    (sensorKeys || []).forEach(s => { m[s.key] = { label: s.name, color: s.color }; });
    return m;
  }, [activeDetail, crossHypOverlays, sensorKeys]);

  const hasAnyLongTickers = hypTickers.long.length > 0;
  const basketIsBroken = hasAnyLongTickers && basketMembers.present.length === 0;
  const basketIsPartial = hasAnyLongTickers &&
    basketMembers.present.length > 0 &&
    basketMembers.missing.length > 0;

  return (
    <div style={{
      background: "linear-gradient(180deg, #0a0a0a 0%, #141414 100%)",
      border: `1px solid ${C.border}`, borderRadius: 14,
      overflow: "hidden", position: "relative",
      fontFamily: "'IBM Plex Mono', monospace", color: C.white,
    }}>
      <style>{FONT_IMPORT}{`
        .rr-hcl-btn:hover { background: rgba(255,255,255,0.06) !important; }
        .rr-hcl-pill:hover { border-color: rgba(255,255,255,0.3) !important; }
      `}</style>

      {/* Grid backdrop */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.4, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />

      {/* ── HEADER ── */}
      <div style={{ padding: isMobile ? "14px 14px 10px" : "18px 24px 14px", borderBottom: `1px solid ${C.border}`, position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10, marginBottom: isMobile ? 10 : 14, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 11, fontWeight: 800, color: C.purple,
            background: "rgba(102,96,254,0.15)", border: "1px solid rgba(102,96,254,0.4)",
            padding: "4px 10px", borderRadius: 3, letterSpacing: "0.18em",
          }}>RR MAGMA</span>
          <span style={{ fontSize: isMobile ? 12 : 14, fontWeight: 800, color: C.white, letterSpacing: "0.08em" }}>
            LABORATÓRIO · HIPÓTESES
          </span>
          <span style={{
            background: "rgba(201,162,39,0.12)", color: C.gold,
            padding: "3px 10px", borderRadius: 3,
            fontSize: 9, fontWeight: 700, letterSpacing: "0.14em",
          }}>CROSS-ASSET DISCOVERY</span>
          {discoveryCount > 0 && (
            <span style={{
              marginLeft: isMobile ? 0 : "auto",
              fontSize: 10, fontWeight: 700, color: C.gold,
              background: "rgba(201,162,39,0.08)", border: "1px solid rgba(201,162,39,0.25)",
              padding: "4px 12px", borderRadius: 4, letterSpacing: "0.06em",
            }}>
              {discoveryCount} correlação{discoveryCount > 1 ? "ões" : ""} forte{discoveryCount > 1 ? "s" : ""}
            </span>
          )}
        </div>

        {activeDetail && (
          <div>
            <h2 style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: TITLE_SIZE, fontWeight: 700, margin: 0, letterSpacing: "-0.015em",
              color: C.white, lineHeight: 1.15,
            }}>
              {activeDetail.id} · {activeDetail.title}
              <span style={{ color: C.dim2 }}> × </span>
              {activeOverlays.length} overlay{activeOverlays.length !== 1 ? "s" : ""}
            </h2>
            <p style={{
              fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic",
              fontSize: isMobile ? 12 : 13, color: C.dim, margin: "6px 0 0", lineHeight: 1.45,
            }}>
              {hasAnyLongTickers
                ? `Basket equal-weight dos ${basketMembers.present.length} tickers LONG com dado disponível. Cruze com commodities, FX, juros ou tickers de outras hipóteses.`
                : "Esta hipótese ainda não tem tickers configurados nas posições — apenas sensores. Use os overlays macro para explorar o contexto de mercado."}
            </p>
          </div>
        )}
      </div>

      {/* ── HYPOTHESIS PICKER ── */}
      <div style={{
        padding: isMobile ? "8px 14px" : "10px 24px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
        position: "relative", overflowX: "auto",
      }}>
        <span style={{ fontSize: 9, letterSpacing: "0.14em", color: C.dim3, fontWeight: 700, marginRight: 4 }}>
          HIPÓTESE:
        </span>
        {hypotheses.map(h => {
          const isActive = h.id === activeHypId;
          return (
            <button key={h.id} onClick={() => setActiveHypId(h.id)}
              className="rr-hcl-btn" style={{
                padding: isMobile ? "7px 12px" : "6px 12px",
                borderRadius: 5,
                border: isActive ? `1px solid ${(h.color || C.gold)}66` : `1px solid ${C.border}`,
                background: isActive ? `${h.color || C.gold}10` : "transparent",
                color: isActive ? (h.color || C.gold) : C.dim,
                fontSize: 10.5, fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
                whiteSpace: "nowrap", transition: "all 0.15s",
              }}>
              {h.id}
            </button>
          );
        })}

        {/* View mode toggle + range picker. On mobile, breaks to its own full-width
            row (marginLeft:auto is neutralized by width:100%) so the segmented
            control and janela buttons don't get squeezed against hypothesis pills. */}
        <div style={{
          marginLeft: isMobile ? 0 : "auto",
          marginTop: isMobile ? 6 : 0,
          width: isMobile ? "100%" : "auto",
          display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap",
        }}>
          {/* View mode toggle — segmented control, purple accent when active */}
          <span style={{ fontSize: 9, letterSpacing: "0.14em", color: C.dim3, fontWeight: 700, marginRight: 6 }}>
            EIXO:
          </span>
          <div style={{
            display: "inline-flex",
            border: `1px solid ${C.purple}55`,
            borderRadius: 5,
            overflow: "hidden",
            marginRight: isMobile ? 8 : 14,
            boxShadow: `0 0 0 1px rgba(102,96,254,0.08)`,
          }}>
            <button onClick={() => setViewMode("pct")}
              className="rr-hcl-btn"
              title="Variação percentual desde o início da janela"
              style={{
                padding: isMobile ? "7px 14px" : "5px 12px",
                border: "none",
                background: viewMode === "pct" ? `${C.purple}25` : "transparent",
                color: viewMode === "pct" ? C.purple : C.dim1,
                fontSize: 10.5, fontWeight: 800, fontFamily: "inherit", cursor: "pointer",
                letterSpacing: "0.06em",
              }}>Δ %</button>
            <button onClick={() => setViewMode("raw")}
              className="rr-hcl-btn"
              title="Valores brutos — escalas heterogêneas entre séries"
              style={{
                padding: isMobile ? "7px 14px" : "5px 12px",
                border: "none",
                borderLeft: `1px solid ${C.purple}33`,
                background: viewMode === "raw" ? `${C.purple}25` : "transparent",
                color: viewMode === "raw" ? C.purple : C.dim1,
                fontSize: 10.5, fontWeight: 800, fontFamily: "inherit", cursor: "pointer",
                letterSpacing: "0.06em",
              }}>RAW</button>
          </div>

          <span style={{ fontSize: 9, letterSpacing: "0.14em", color: C.dim3, fontWeight: 700, marginRight: 6 }}>
            JANELA:
          </span>
          {RANGES.map(r => {
            const isActive = r.id === rangeId;
            return (
              <button key={r.id} onClick={() => setRangeId(r.id)}
                className="rr-hcl-btn" style={{
                  padding: isMobile ? "6px 11px" : "5px 10px",
                  borderRadius: 4,
                  border: isActive ? "1px solid rgba(255,255,255,0.25)" : `1px solid ${C.border}`,
                  background: isActive ? "rgba(255,255,255,0.04)" : "transparent",
                  color: isActive ? C.white : C.dim2,
                  fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
                  letterSpacing: "0.06em",
                }}>{r.label}</button>
            );
          })}
        </div>
      </div>

      {/* ── BASKET COMPOSITION with status pills ── */}
      {activeDetail && hasAnyLongTickers && (
        <div style={{
          padding: isMobile ? "8px 14px" : "8px 24px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
          position: "relative",
        }}>
          <span style={{ fontSize: 9, letterSpacing: "0.14em", color: C.dim3, fontWeight: 700 }}>BASKET:</span>
          {hypTickers.long.map(t => {
            const status = tickerStatus[t];
            const isMissing = status === "failed";
            const color = activeDetail.color || C.gold;
            return (
              <span key={t} title={isMissing ? `${t}: sem dado no :6062/:8080` : undefined} style={{
                padding: "3px 10px", borderRadius: 3,
                background: isMissing ? "rgba(239,68,68,0.08)" : `${color}10`,
                border: `1px solid ${isMissing ? "rgba(239,68,68,0.35)" : color + "30"}`,
                color: isMissing ? C.red : color,
                fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                display: "inline-flex", alignItems: "center", gap: 5,
                textDecoration: isMissing ? "line-through" : "none",
              }}>
                {t}
                {isMissing && <span style={{ fontSize: 8 }}>✕</span>}
              </span>
            );
          })}
          <span style={{ fontSize: 9, color: C.dim3, marginLeft: 4 }}>
            equal-weight · {viewMode === "pct" ? "Δ % desde início da janela" : "valores brutos"}
            {basketIsPartial && (
              <span style={{ color: C.amber, marginLeft: 8 }}>
                · {basketMembers.present.length}/{hypTickers.long.length} com dado
              </span>
            )}
          </span>
        </div>
      )}

      {/* ── OVERLAY PICKER ── */}
      <div style={{ padding: isMobile ? "10px 14px" : "12px 24px", borderBottom: `1px solid ${C.border}`, position: "relative" }}>
        {MACRO_GROUPS.map(g => (
          <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ minWidth: LABEL_MIN_W, fontSize: 8.5, letterSpacing: "0.14em", color: C.dim3, fontWeight: 700 }}>
              {g.label.toUpperCase()}
            </span>
            {g.items.map(item => {
              const isActive = activeOverlays.includes(item.ticker);
              const isFailed = tickerStatus[item.ticker] === "failed";
              return (
                <button key={item.ticker} onClick={() => toggleOverlay(item.ticker)}
                  className="rr-hcl-pill" style={{
                    padding: isMobile ? "6px 11px" : "5px 11px",
                    borderRadius: 4,
                    border: `1px solid ${isActive ? item.color + "80" : C.border}`,
                    background: isActive ? `${item.color}15` : "rgba(255,255,255,0.02)",
                    color: isActive ? item.color : C.dim,
                    fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    transition: "all 0.15s", whiteSpace: "nowrap",
                    opacity: isFailed && isActive ? 0.5 : 1,
                  }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: isActive ? item.color : C.dim3 }} />
                  {item.label}
                  {isActive && isFailed && <span style={{ fontSize: 8, color: C.red }}>✕</span>}
                </button>
              );
            })}
          </div>
        ))}

        {/* Cross-hypothesis tickers */}
        {crossHypOverlays.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ minWidth: LABEL_MIN_W, fontSize: 8.5, letterSpacing: "0.14em", color: C.dim3, fontWeight: 700 }}>
              OUTRAS HYP
            </span>
            {crossHypOverlays.slice(0, 12).map(item => {
              const isActive = activeOverlays.includes(item.ticker);
              return (
                <button key={item.ticker} onClick={() => toggleOverlay(item.ticker)}
                  className="rr-hcl-pill" style={{
                    padding: isMobile ? "6px 11px" : "5px 11px",
                    borderRadius: 4,
                    border: `1px solid ${isActive ? item.color + "80" : C.border}`,
                    background: isActive ? `${item.color}15` : "rgba(255,255,255,0.02)",
                    color: isActive ? item.color : C.dim,
                    fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    transition: "all 0.15s", whiteSpace: "nowrap",
                  }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: isActive ? item.color : C.dim3 }} />
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── CHART ── */}
      <div style={{ padding: isMobile ? "8px 2px 0" : "10px 6px 0", position: "relative" }}>
        {loading ? (
          <div style={{ height: CHART_H, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 10, color: C.dim3 }}>buscando OHLCV mensal...</span>
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ height: CHART_H, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: C.dim2 }}>sem dados para o período selecionado</span>
            {basketIsBroken && (
              <span style={{ fontSize: 10, color: C.red, fontStyle: "italic", fontFamily: "'Fraunces', Georgia, serif" }}>
                nenhum dos {hypTickers.long.length} tickers LONG dessa hipótese retornou dado
              </span>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={CHART_H}>
            <LineChart data={chartData} margin={{ top: 8, right: isMobile ? 10 : 24, left: 2, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
              <XAxis dataKey="ym" stroke="#444" fontSize={8.5} tickLine={false} axisLine={false}
                interval="preserveStartEnd" minTickGap={isMobile ? 40 : 60} />
              <YAxis stroke="#444" fontSize={8.5} tickLine={false} axisLine={false}
                domain={viewMode === "pct" ? ["dataMin - 3", "dataMax + 3"] : ["auto", "auto"]}
                tickFormatter={v => {
                  if (viewMode === "pct") return `${v >= 0 ? "+" : ""}${v.toFixed(0)}%`;
                  // Raw mode: compact large numbers so ^BVSP at 130k doesn't need 6-digit ticks
                  const abs = Math.abs(v);
                  if (abs >= 1000) return (v / 1000).toFixed(1) + "k";
                  if (abs >= 10) return v.toFixed(0);
                  return v.toFixed(2);
                }}
                width={Y_AXIS_W}
              />
              <ReferenceLine
                y={viewMode === "pct" ? 0 : undefined}
                stroke="rgba(255,255,255,0.15)"
                strokeDasharray="4 4"
                label={viewMode === "pct"
                  ? { value: "início janela", fill: "rgba(255,255,255,0.25)", fontSize: 8, position: "insideTopRight" }
                  : undefined
                }
              />
              <Tooltip
                content={<ChartTooltip overlayLookup={overlayLookup} viewMode={viewMode} />}
                wrapperStyle={isMobile ? { maxWidth: "calc(100vw - 40px)", zIndex: 20 } : undefined}
              />
              <Line type="monotone" dataKey="basket" stroke={activeDetail?.color || C.gold}
                strokeWidth={2.5} dot={false} connectNulls
                activeDot={{ r: 4, fill: activeDetail?.color || C.gold, stroke: "#000", strokeWidth: 2 }} />
              {activeOverlays.map(t => {
                const item = overlayLookup[t];
                return (
                  <Line key={t} type="monotone" dataKey={t}
                    stroke={item?.color || C.dim} strokeWidth={1.5}
                    strokeDasharray="5 3" dot={false} connectNulls />
                );
              })}
              {(sensorKeys || []).map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  stroke={s.color || C.amber}
                  strokeWidth={1.5}
                  strokeDasharray="2 4"
                  dot={{ r: 2.5, fill: s.color || C.amber, stroke: "none" }}
                  activeDot={{ r: 4, fill: s.color || C.amber, stroke: "#000", strokeWidth: 1.5 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
        {viewMode === "raw" && chartData.length > 0 && (
          <div style={{
            margin: isMobile ? "0 14px" : "0 24px",
            padding: "4px 10px", marginTop: 4,
            background: "rgba(251,191,36,0.05)", border: "1px solid rgba(251,191,36,0.15)",
            borderRadius: 3, fontSize: 9, color: "rgba(251,191,36,0.7)",
            fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic", textAlign: "center",
          }}>
            modo RAW · escalas heterogêneas — séries de magnitude diferente podem parecer planas. Use Δ % pra comparação visual.
          </div>
        )}
      </div>

      {/* ── CORRELATION TABLE ── */}
      {(activeOverlays.length > 0 || sensorKeys?.length > 0) && (
        <div style={{ margin: isMobile ? "0 14px" : "0 24px", padding: "14px 0", borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.dim3, letterSpacing: "0.14em", marginBottom: 10 }}>
            CORRELAÇÃO PEARSON · RETORNOS MENSAIS · {range.label} · {chartData.length} meses
          </div>

          <div style={{
            display: "grid", gridTemplateColumns: CORR_COLS,
            gap: 4, padding: "6px 10px",
            background: "rgba(255,255,255,0.02)", borderRadius: 4, marginBottom: 3,
          }}>
            {(isMobile
              ? ["OVERLAY", "CORREL", viewMode === "pct" ? `RET ${range.label}` : "ATUAL"]
              : ["OVERLAY", "CORREL", "FORÇA", viewMode === "pct" ? `RET ${range.label}` : "ATUAL"]
            ).map(h => (
              <span key={h} style={{ fontSize: 8, fontWeight: 700, color: C.dim3, letterSpacing: "0.12em" }}>{h}</span>
            ))}
          </div>

          {activeOverlays.map((t, i) => {
            const item = overlayLookup[t] || { label: t, color: C.dim };
            const corr = correlations[t];
            const ret = returnsPct[t];
            const raw = lastRawValues[t];
            const strength = corr != null ? corrStrength(corr) : null;
            const isFailed = tickerStatus[t] === "failed";
            const lastColValue = viewMode === "pct" ? ret : raw;
            const lastColColor = viewMode === "pct"
              ? (ret == null ? C.dim3 : (ret >= 0 ? C.green : C.red))
              : (raw == null ? C.dim3 : C.white);
            const fmtLast = (v) => {
              if (v == null) return "—";
              if (viewMode === "pct") return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
              const abs = Math.abs(v);
              if (abs >= 1000) return v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
              if (abs >= 10) return v.toFixed(2);
              return v.toFixed(4);
            };
            return (
              <div key={t} style={{
                display: "grid", gridTemplateColumns: CORR_COLS,
                gap: 4, padding: "8px 10px",
                borderBottom: i < activeOverlays.length - 1 ? `1px solid ${C.borderSoft}` : "none",
                alignItems: "center",
                opacity: isFailed ? 0.5 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.white,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{item.label}</span>
                  {isFailed && !isMobile && <span style={{ fontSize: 9, color: C.red }}>sem dado</span>}
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: corr == null ? C.dim3 : (corr >= 0 ? C.green : C.red),
                }}>
                  {corr == null ? "—" : `${corr >= 0 ? "+" : ""}${corr.toFixed(3)}`}
                </span>
                {!isMobile && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
                    color: strength?.color || C.dim3,
                  }}>{strength?.label || "—"}</span>
                )}
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: lastColColor,
                  textAlign: isMobile ? "right" : "left",
                }}>
                  {fmtLast(lastColValue)}
                </span>
              </div>
            );
          })}

          {(sensorKeys || []).map((s, i) => {
            const ret = returnsPct[s.key];
            const raw = lastRawValues[s.key];
            const lastColValue = viewMode === "pct" ? ret : raw;
            const lastColColor = viewMode === "pct"
              ? (ret == null ? C.dim3 : (ret >= 0 ? C.green : C.red))
              : (raw == null ? C.dim3 : C.white);
            const fmtLast = (v) => {
              if (v == null) return "—";
              if (viewMode === "pct") return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
              const abs = Math.abs(v);
              if (abs >= 1000) return v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
              if (abs >= 10) return v.toFixed(2);
              return v.toFixed(4);
            };
            return (
              <div key={s.key} style={{
                display: "grid", gridTemplateColumns: CORR_COLS,
                gap: 4, padding: "8px 10px",
                borderBottom: i < sensorKeys.length - 1 ? `1px solid ${C.borderSoft}` : "none",
                alignItems: "center",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.white,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{s.name}</span>
                  {!isMobile && <span style={{ fontSize: 9, color: C.dim3 }}>· sensor</span>}
                </div>
                <span style={{ fontSize: 11, color: C.dim3 }}>—</span>
                {!isMobile && (
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: C.dim3 }}>SENSOR</span>
                )}
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: lastColColor,
                  textAlign: isMobile ? "right" : "left",
                }}>
                  {fmtLast(lastColValue)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── FOOTER ── */}
      <div style={{
        margin: isMobile ? "0 14px 12px" : "0 24px 16px", padding: "8px 12px",
        background: "rgba(245,200,66,0.05)", border: "1px solid rgba(245,200,66,0.1)",
        borderRadius: 4,
      }}>
        <p style={{
          fontSize: 8.5, color: "rgba(245,200,66,0.6)",
          lineHeight: 1.45, margin: 0, fontFamily: "inherit",
        }}>
          <strong style={{ color: C.amber }}>⚠ AVISO:</strong>{" "}
          Correlações passadas não determinam movimentos futuros.
          Basket equal-weight dos tickers LONG com dado disponível na janela.
          Dados: indices_etfs :6062 (batch cache, monthly OHLC) · {chartData.length} meses observados.
        </p>
      </div>
    </div>
  );
}